import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react"
import { useMarkHomeInterested } from "../../hooks/usePortfolioQueries"
import { formatCurrency, formatPayback, formatRoi, getRoiColor } from "../../utils/formatters"
import { CardDivider, CardMetric } from "../cards/CardParts"
import { StreetViewImage } from "../common/StreetViewImage"

export interface HomeForCard {
  id: number
  fullAddress: string
  imageUrl?: string | null
  upgradesRoi?: number | null
  upgradesTotalCost?: number | null
  upgradesPaybackPeriod?: number | null
  features?: { [key: string]: unknown } | null
  kanbanStage?: string | null
}

const HomeOpportunityCard: React.FC<{
  home: HomeForCard
  kanbanStage?: string | null
  onClick: (id: number) => void
  hideInterested?: boolean
}> = ({ home, kanbanStage, onClick, hideInterested = false }) => {
  const roiColor = getRoiColor(home.upgradesRoi)
  const markInterested = useMarkHomeInterested()
  const isInterested = (kanbanStage ?? home.kanbanStage) === "interested"

  const handleInterested = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isInterested) {
      markInterested.mutate(home.id)
    }
  }

  const propertyDetailLine = getHomeOpportunityDetailLine(home)

  return (
    <Box
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.100"
      cursor="pointer"
      transition="all 0.15s"
      _hover={{ boxShadow: "md", borderColor: "green.200", transform: "translateY(-1px)" }}
      onClick={() => onClick(home.id)}
    >
      {/* Desktop */}
      <HStack gap={0} align="stretch" display={{ base: "none", md: "flex" }}>
        <Box w="160px" minW="160px" flexShrink={0} bg="gray.100" overflow="hidden">
          <StreetViewImage
            imageUrl={home.imageUrl}
            address={home.fullAddress}
            svSize="400x300"
            alt={home.fullAddress}
            w="100%" h="100%" minH="90px"
            objectFit="cover"
          />
        </Box>

        <VStack align="start" justify="center" gap={1} flex={1} px={5} py={4}>
          <Text fontWeight="semibold" fontSize="md" color="gray.900" lineClamp={1}>
            {home.fullAddress}
          </Text>
          <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
        </VStack>

        <HStack gap={0} align="center" pr={5} flexShrink={0}>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Green ROI"
              value={<Text fontSize="lg" fontWeight="semibold" color={roiColor}>{formatRoi(home.upgradesRoi)}</Text>}
            />
          </Box>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Upgrade Cost"
              value={<Text fontSize="lg" fontWeight="semibold" color="gray.800">{formatCurrency(home.upgradesTotalCost)}</Text>}
            />
          </Box>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Payback"
              value={<Text fontSize="lg" fontWeight="semibold" color="gray.800">{formatPayback(home.upgradesPaybackPeriod)}</Text>}
            />
          </Box>
          {!hideInterested && (
            <>
              <CardDivider />
              <Box pl={5}>
                <Button
                  colorPalette="green"
                  variant={isInterested ? "solid" : "outline"}
                  size="sm"
                  loading={markInterested.isPending}
                  disabled={isInterested || markInterested.isPending}
                  onClick={handleInterested}
                >
                  {isInterested ? "Interested" : "Mark as Interested"}
                </Button>
              </Box>
            </>
          )}
        </HStack>
      </HStack>

      {/* Mobile */}
      <VStack align="stretch" gap={0} display={{ base: "flex", md: "none" }}>
        <StreetViewImage
          imageUrl={home.imageUrl}
          address={home.fullAddress}
          svSize="400x300"
          alt={home.fullAddress}
          w="100%" h="180px"
          objectFit="cover"
        />
        <VStack align="stretch" gap={3} p={4}>
          <VStack align="start" gap={0.5}>
            <Text fontWeight="semibold" fontSize="md" color="gray.900">{home.fullAddress}</Text>
            <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
          </VStack>
          <HStack gap={6} flexWrap="wrap">
            <CardMetric
              label="Green ROI"
              value={<Text fontSize="md" fontWeight="semibold" color={roiColor}>{formatRoi(home.upgradesRoi)}</Text>}
            />
            <CardMetric
              label="Upgrade Cost"
              value={<Text fontSize="md" fontWeight="semibold" color="gray.800">{formatCurrency(home.upgradesTotalCost)}</Text>}
            />
            <CardMetric
              label="Payback"
              value={<Text fontSize="md" fontWeight="semibold" color="gray.800">{formatPayback(home.upgradesPaybackPeriod)}</Text>}
            />
          </HStack>
          {!hideInterested && (
            <Button
              colorPalette="green"
              variant={isInterested ? "solid" : "outline"}
              size="sm"
              loading={markInterested.isPending}
              disabled={isInterested || markInterested.isPending}
              alignSelf="flex-start"
              onClick={handleInterested}
            >
              {isInterested ? "Interested" : "Mark as Interested"}
            </Button>
          )}
        </VStack>
      </VStack>
    </Box>
  )
}

export const getHomeOpportunityDetailLine = (home: HomeForCard): string => {
  const f = home.features ?? {}
  return [
    f.numberOfHabitableRooms != null ? `${f.numberOfHabitableRooms} bed` : null,
    f.propertyType ? String(f.propertyType) : null,
    f.floorArea != null ? `${Math.round(f.floorArea as number)} m²` : null,
  ].filter(Boolean).join(" · ") || "Details unavailable"
}

export default HomeOpportunityCard
